import "./button.css";

// Button — momentary or toggle. DOM-rendered so it retains native focus
// behavior, keyboard activation, and accessibility traits. CSS owns the
// visual state machine; this module only wires up events and class toggles.

/** Minimum time (ms) the `is-pressed` class stays applied after a click,
 * even if the user releases almost immediately. Without this, fast clicks
 * leave the depression invisible. Action callbacks still fire on the real
 * pointer release so input feels responsive — only the visual lingers. */
const MIN_PRESS_VISUAL_MS = 100;
//
// Variants:
//   momentary  — fires `onPress` on pointerdown / Space / Enter, `onRelease`
//                on the matching release. Visually inset only while held.
//   toggle     — flips `active` on pointerup / Space / Enter. Active state
//                is persistent and shows the configured intent glow.
//
// Intents (used only when toggled on):
//   blue   — ice-blue glow. Default. Selection-style toggles.
//   green  — status-green glow. On/off indicators (oscillator enable, etc.).

export type ButtonVariant = "momentary" | "toggle";
export type ButtonIntent = "blue" | "green" | "red";

export type ForcedButtonState =
  | "idle"
  | "hover"
  | "pressed"
  | "toggled"
  | "toggled-pressed"
  | "disabled";

export interface ButtonOptions {
  container: HTMLElement;
  label?: string;
  variant?: ButtonVariant;
  intent?: ButtonIntent;
  /** Initial active state for toggles. */
  active?: boolean;
  onPress?: () => void;
  onRelease?: () => void;
  onToggle?: (active: boolean) => void;
  /** Force a visual state for the demo page. Disables interaction. */
  forceState?: ForcedButtonState;
  /** Pixel width override. Default size suits short labels. */
  width?: number;
}

export class SynthButton {
  private root: HTMLButtonElement;
  private variant: ButtonVariant;
  private intent: ButtonIntent;
  private active: boolean;
  private pressed = false;
  private forced: ForcedButtonState | undefined;

  private onPress: (() => void) | undefined;
  private onRelease: (() => void) | undefined;
  private onToggle: ((active: boolean) => void) | undefined;

  private pressStartTime = 0;
  private pressReleaseTimer: number | undefined;

  constructor(opts: ButtonOptions) {
    this.variant = opts.variant ?? "momentary";
    this.intent = opts.intent ?? "blue";
    this.active = opts.active ?? false;
    this.forced = opts.forceState;
    if (opts.onPress) this.onPress = opts.onPress;
    if (opts.onRelease) this.onRelease = opts.onRelease;
    if (opts.onToggle) this.onToggle = opts.onToggle;

    this.root = document.createElement("button");
    this.root.type = "button";
    this.root.className = "synth-btn";
    this.root.dataset["intent"] = this.intent;
    this.root.dataset["variant"] = this.variant;
    if (opts.width) this.root.style.minWidth = `${opts.width}px`;
    if (opts.label) this.root.textContent = opts.label;

    opts.container.append(this.root);

    this.applyForcedState();
    this.refreshState();

    if (!this.forced) this.attachInteraction();
  }

  setActive(active: boolean): void {
    if (this.variant !== "toggle") return;
    if (this.active === active) return;
    this.active = active;
    this.refreshState();
    this.onToggle?.(active);
  }

  isActive(): boolean {
    return this.active;
  }

  /** Runtime disable/enable. Distinct from `forceState: "disabled"`,
   *  which is for the static demo page — this is for live UI gating
   *  (e.g. the preset bar's delete button when on a factory preset). */
  setDisabled(disabled: boolean): void {
    this.root.disabled = disabled;
  }

  destroy(): void {
    if (this.pressReleaseTimer !== undefined) {
      clearTimeout(this.pressReleaseTimer);
    }
    this.root.remove();
  }

  // ----- interaction -----

  private startPress(): void {
    if (this.pressed) return;
    // Cancel any in-flight visual-release timer (rare: rapid re-press).
    if (this.pressReleaseTimer !== undefined) {
      clearTimeout(this.pressReleaseTimer);
      this.pressReleaseTimer = undefined;
    }
    this.pressed = true;
    this.pressStartTime = performance.now();
    this.refreshState();
    if (this.variant === "momentary") this.onPress?.();
  }

  /** End a press. Action callbacks fire immediately; the visual depression
   * stays applied for at least `MIN_PRESS_VISUAL_MS` so a fast click is
   * actually seen. `fired` is true for normal release (pointerup, keyup),
   * false for cancellations (pointercancel, leaving the button while held). */
  private endPress(fired: boolean): void {
    if (!this.pressed) return;
    if (fired) {
      if (this.variant === "momentary") {
        this.onRelease?.();
      } else {
        this.setActive(!this.active);
      }
    }
    const elapsed = performance.now() - this.pressStartTime;
    const remaining = Math.max(0, MIN_PRESS_VISUAL_MS - elapsed);
    if (remaining > 0) {
      this.pressReleaseTimer = window.setTimeout(() => {
        this.pressed = false;
        this.pressReleaseTimer = undefined;
        this.refreshState();
      }, remaining);
    } else {
      this.pressed = false;
      this.refreshState();
    }
  }

  private attachInteraction(): void {
    this.root.addEventListener("pointerdown", (ev) => {
      ev.preventDefault();
      this.root.setPointerCapture(ev.pointerId);
      this.startPress();
    });

    const onPointerEnd = (ev: PointerEvent, fired: boolean): void => {
      if (!this.pressed) return;
      this.root.releasePointerCapture(ev.pointerId);
      this.endPress(fired);
    };
    this.root.addEventListener("pointerup", (ev) => onPointerEnd(ev, true));
    this.root.addEventListener("pointercancel", (ev) => onPointerEnd(ev, false));
    this.root.addEventListener("pointerleave", (ev) => {
      // Leaving while held cancels the press. Matches macOS button feel.
      if (this.pressed) onPointerEnd(ev, false);
    });

    // Keyboard activation. Native <button> already fires `click` on Space/
    // Enter; we just need to mirror the visual press for the time the key
    // is held.
    this.root.addEventListener("keydown", (ev) => {
      if (ev.repeat) return;
      if (ev.key !== " " && ev.key !== "Enter") return;
      this.startPress();
    });
    this.root.addEventListener("keyup", (ev) => {
      if (ev.key !== " " && ev.key !== "Enter") return;
      this.endPress(true);
    });
  }

  private applyForcedState(): void {
    if (!this.forced) return;
    switch (this.forced) {
      case "hover":
        this.root.classList.add("is-forced-hover");
        break;
      case "pressed":
        this.pressed = true;
        break;
      case "toggled":
        this.active = true;
        break;
      case "toggled-pressed":
        this.active = true;
        this.pressed = true;
        break;
      case "disabled":
        this.root.disabled = true;
        break;
    }
  }

  private refreshState(): void {
    this.root.classList.toggle("is-pressed", this.pressed);
    this.root.classList.toggle("is-active", this.active);
  }
}
